import React, {useCallback, useEffect, useState} from 'react';
import styles from './styles.module.scss';
import {Col, Form} from 'antd';
import {linkgraphDomains, openUrl} from '@/utils/router';
import {AccountInput, ContinueButton, FormContainer, StyledFormItem, Terms, onlyLettersReg, onlySpacesReg, HeadingAnimationWrapper} from './helpers';
import GoogleAuthButton from '@/components/common-components/components/google-auth-button';
import {getDomain, getGoogleRedirectUrl, getSingleUrlParam} from '@/utils/url';
import {observer} from 'mobx-react';
import {useStore} from '@/store/root-store';
import styled from 'styled-components';
import {authApi} from '@/api/account';
import {antdNotification} from '@/utils/notification';
import {apiError} from '@/utils/api';
import {metaClickEvents} from '@/utils/functions';
import {updateGTMDatalayer} from '@/utils/gtm';
import {NextImg} from '@/utils/nextImg';
import {faEnvelope, faUser} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import Link from 'next/link';
import {getLocalesByKey as t} from '@/utils/translation';
import {LogoWrapper} from '../components/logo-wrapper/logoWrapper';
import {RegisterRating} from '../rating';


const StepOne = observer(({setStepData, stepData}) => {
  const [form] = Form.useForm();
  const currentDomain = getDomain(window.location.hostname);
  const isWhiteLabelDomain = !linkgraphDomains.includes(currentDomain);
  const {settings: {customer: {profile: {logo, signUpLogo}}}} = useStore('');
  const invitationToken = getSingleUrlParam('invitation_token');
  const [emailDisabled, setEmailDisabled] = useState(false);

  const nameParam = getSingleUrlParam('name');
  const emailParam = getSingleUrlParam('email');

  const {settings: {customer: {profile: {domainApiLoading, brandColor}}}} = useStore('');

  useEffect(() => {
    if (nameParam || emailParam) {
      form.setFieldsValue({
        contactName: nameParam,
        email: emailParam,
      });
    }
  }, [nameParam, emailParam]);

  const loadEmail = async () => {
    if (invitationToken) {
      setEmailDisabled(true);
      try {
        const response = await authApi.loadRegisterEmail(invitationToken);
        form.setFieldsValue({
          email: response?.data?.email || '',
        });
      } catch (e) {
        const errorMessage = apiError(e);
        antdNotification.error(errorMessage, 'topRight');
      }
    }
  };

  useEffect(() => {
    loadEmail();
  }, []);

  const handleGoogleRedirect = useCallback(
    () => {
      const url = getGoogleRedirectUrl();
      openUrl(url);
    },
    [],
  );

  return (
    <>
      {(isWhiteLabelDomain && domainApiLoading) ? <></> : <div className={styles?.signUpBg} style={{background: isWhiteLabelDomain && 'url("") no-repeat center center'}}>
        {(isWhiteLabelDomain && brandColor) && (
          <>
            <div className={styles.bgBack}></div>
            <div className={styles.bgFront} style={{'--brand-color': brandColor} as any}></div>
          </>
        )}
        <div
          className={styles.bgGradient}
        />
        <div
          style={{height: '22px'}}
        />
        <LogoWrapper>
          <NextImgStyled className={invitationToken && styles.invitationLogo} src={isWhiteLabelDomain ? (signUpLogo ?? logo) : '/img/searchatlasLogo.svg'} />
        </LogoWrapper>
        {invitationToken ? <div className={styles.invitationTitle}>Accept your Invitation</div> : <div className={styles.descriptionContainer}>
          <div className={styles.container}>
            <HeadingAnimationWrapper>
              <div style={{padding: '0 20px'}}>
                <p className={styles.text}>Automate your SEO {isWhiteLabelDomain ? '' : 'with '} {isWhiteLabelDomain ? '' : <span className={styles.descLineTwo}> Search Atlas</span>}.</p>
              </div>
            </HeadingAnimationWrapper>
            <div id='console' className='console-underscore'></div>
          </div>
          <div className={styles.descriptionWrapper}>
            <div className={styles.description}>Our SEO tools automate thousands of hours of work to deliver organic growth quickly.</div>
            <div className={styles.description}>Stop wasting time and money on your SEO and transform your strategy in 7 days.</div>
          </div>
        </div>}
        <FormContainer brandColor={(isWhiteLabelDomain && brandColor) ? brandColor : ''}>
          {!invitationToken && <div className={styles.formTitle} style={{justifyContent: 'center'}}>Start your&nbsp;<div style={{color: '#9754CE'}}>7-day free</div>&nbsp;trial</div>}
          <StyledForm style={{marginTop: invitationToken ? '30px' : ''}} className={styles.form} form={form}
            onFinish={(values: any) => {
              setStepData({step: 'stepTwo', data: {...stepData.data, ...values}});
              metaClickEvents('click_register_step_1', {step: 'step 1', email: values?.email, contactName: values?.contactName});
              updateGTMDatalayer({
                event: invitationToken ? 'Activate Your Account' : 'Continue with email',
              });
              updateGTMDatalayer({'event': 'email added', 'step': 'step 1', 'email': values?.email, 'contactName': values?.contactName});
            }}
            initialValues={{contactName: stepData?.data ? stepData.data.contactName : '', email: stepData?.data ? stepData.data.email : ''}}
          >
            <Col>
              <Col span={24}>
                <StyledFormItem
                  name='contactName'
                  rules={[
                    {
                      required: true,
                      message: 'Please enter your name',
                    },
                    {
                      whitespace: true,
                      message: 'Only Empty Spaces are not allowed',
                    },
                  ]}
                >
                  <AccountInput
                    type='text'
                    prefix={<FontAwesomeIcon icon={faUser} color='#4E5156' style={{marginRight: '15px'}}/>}
                    onChange={e=>{
                      const chars = e.target.value.split('');
                      const char = chars.pop();
                      if (!onlyLettersReg.test(char) && !onlySpacesReg.test(char)) {
                        e.target.value = chars.join('');
                      }
                    }}
                    placeholder={'Full name'}
                  />
                </StyledFormItem>
              </Col>
            </Col>
            <Col className={styles.emailInput} >
              <Col>
                <StyledFormItem
                  name='email'
                  rules={[
                    {
                      required: true,
                      message: 'Please enter an email',
                    },
                    {
                      type: 'email',
                      message: 'Please enter a valid email address',
                    },
                  ]}
                >
                  <AccountInput
                    data-cy='emailInput'
                    prefix={<FontAwesomeIcon icon={faEnvelope} color='#4E5156' style={{marginRight: '15px'}}/>}
                    disabled={emailDisabled}
                    type='email'
                    placeholder={'Email address'}
                  />
                </StyledFormItem>
              </Col>
            </Col>
            <AccountInput type='hidden' id='gclid'/>
            <Col>
              <ContinueButton brandColor={(isWhiteLabelDomain && brandColor) ? brandColor : ''} htmlType='submit' style={{marginBottom: isWhiteLabelDomain ? '50px' : '0px'}}
              >
                <div>
                  {invitationToken ? 'Activate Your Account' : 'Continue with email'}
                </div>
                <NextImg src='/img/registerBg/searchAtlasIcon.svg' width={16} height={16} />
              </ContinueButton>
            </Col>
            {invitationToken ? null : <>
              {!isWhiteLabelDomain && <><Col>
                <div className={styles.or}>or</div>
              </Col>
              <Col>
                <GoogleAuthButton responsiveMedia={window.screen.width < 700 ? '' : '@media screen and (max-width: 800px) {height: 48px !important; font-size: 14px !important;}'} style={{borderRadius: '10px', backgroundColor: '#E3E3E8'}} onClick={handleGoogleRedirect} />
              </Col></>}
            </>}
            {!isWhiteLabelDomain && <Col>
              <Terms>
                <div>By continuing, you agree to Search Atlas’s <a href={`${process.env.LINKGRAPH_COM}/terms-of-service/`} target='_blank' rel='noreferrer'>Consumer Terms</a></div>
                <div>and acknowledge their <a href={`${process.env.LINKGRAPH_COM}/privacy-policy/`} target='_blank' rel='noreferrer'>Privacy Policy</a>.</div>
              </Terms>
            </Col>}
            <Col>
              <div className={styles?.footerContainer}>
                <RegisterRating showLowerSection value={5} marginRight={'15px'}/>
                <img style={{width: '70%', height: 28}} src={'/img/registerBg/footer-icons-2.svg'} />
              </div>
            </Col>
          </StyledForm>
        </FormContainer>
        <Col style={{zIndex: 2}}>
          <p className={styles.logIn} style={{marginBottom: isWhiteLabelDomain ? '47px' : ''}}>
            {t('already-have-account')}{' '}<Link href='/login'>{t('log-in')}</Link>
          </p>
        </Col>
      </div>}
    </>
  );
});

export default StepOne;

const NextImgStyled = styled.img`
  width: 260px;
  height: auto;
  position: relative;
  z-index: 3;
  margin-bottom: 47px;
  margin-top: 40px;
  @media screen and (max-width: 810px) {
    margin-top: 16px !important;
    margin-bottom: 28px !important;
    width: 220px;
  }
  @media screen and (max-width: 600px) {
    margin-top: 10px !important;
    margin-bottom: 40px !important;
    width: 200px;
  }
`;

const StyledForm = styled(Form)`
  width: 100%;
  @media screen and (max-width: 600px) {
    width: 90% !important;
  }
`;
